/** Delay before showing the "Start Now" button on match found overlay */
export const BUTTON_REVEAL_DELAY_MS = 1500;

/** Delay before AI makes its move in local games */
export const AI_MOVE_DELAY_MS = 300;

/** Delay before the AI responds to a draw offer in local games */
export const AI_DRAW_DELAY_MS = 600;

/** Duration to show the "computer declined the draw" notice before clearing */
export const AI_DRAW_DECLINE_NOTICE_MS = 2500;

/**
 * Durata della pastiglia "l'avversario ha rifiutato la patta" online.
 *
 * Piu' lunga dei 2,5s del rifai-mossa: quella risponde a un gesto appena fatto,
 * questa arriva mentre si sta gia' pensando alla mossa dopo, e va letta con la
 * coda dell'occhio.
 */
export const DRAW_DECLINE_NOTICE_MS = 4000;

/**
 * Quanto la barra di gioco resta il saluto dell'avversario («Mario: Buon gioco!»).
 *
 * I secondi non misurano «quanto ci metti a leggere» ma «per quanto la barra non e'
 * la barra»: il saluto PRENDE IL POSTO di Abbandona, Patta, Focus e Impostazioni,
 * come fa l'offerta di patta. Cinque bastano a leggere un nome e a decidere se
 * rispondere con un tocco; dieci — quanti ne aveva la vecchia pastiglia, che pero'
 * galleggiava sopra i comandi senza toglierli — sarebbero comandi tenuti in ostaggio
 * da una cortesia.
 */
export const SALUTO_RICEVUTO_MS = 5000;

/**
 * Quanto la barra resta l'invito «Buon gioco!» prima di restituire i comandi.
 *
 * Stessa ragione, altro verso: chi vuole salutare lo fa nei primi secondi, e chi non
 * vuole non deve muovere una pedina per riavere Abbandona e Impostazioni — a cadenza
 * lunga l'attesa della prima mossa dura minuti. Scaduto il tempo l'invito non torna:
 * il saluto d'apertura ha una finestra sola.
 */
export const SALUTO_INVITO_MS = 10000;

/** Quanto resta la conferma «Saluto inviato» prima che la barra torni quella dei comandi. */
export const SALUTO_CONFERMA_MS = 2000;

/** Duration to show error messages before auto-dismissing */
export const ERROR_DISMISS_MS = 3000;

/** Duration of the position bar highlight animation after points increase */
export const POSITION_HIGHLIGHT_MS = 2000;

/** Duration to display rank delta indicators before clearing */
export const DELTA_DISPLAY_MS = 2000;

/** Delay before scrolling to user's row in standings */
export const SCROLL_DELAY_MS = 100;

/** Intervallo di refresh della lista giocatori online (sfida diretta) */
export const ONLINE_USERS_REFRESH_MS = 30000;

/**
 * Quanto si aspetta, a socket caduto, prima di dichiarare SCADUTA la presenza —
 * cioe' di buttare via i tavoli della lobby e la lista dei collegati.
 *
 * Quelle due liste sono la fotografia di un server vivo: senza filo non sono una
 * lista vecchia, non sono una lista. Finche' nessuno le invalidava, un telefono
 * portato fuori rete continuava a mostrare i tavoli e i giocatori dell'ultima
 * connessione a tempo indeterminato, coi bottoni «Siediti» accesi sopra tavoli
 * che lato server erano morti da un pezzo (l'host lo perde con 0 ms di attesa,
 * `handleLobbyDisconnect`).
 *
 * Il numero e' lo stesso di `ATTESA_PRIMA_DI_MOSTRARSI_MS` in
 * `ConnessionePersaBanner`, e per la stessa ragione: un singhiozzo di rete dura
 * meno di cosi', e una lista che si svuota e si ripopola a ogni riconnessione da
 * un secondo e' rumore. Le due costanti si tengono in pari.
 *
 * L'altra meta' del problema non passa da qui e non aspetta niente: quando il
 * sistema operativo DICHIARA di non avere rete (`useScadenzaPresenza`) la
 * presenza scade subito, perche' il SO non sfarfalla — vedi `useSenzaRete`.
 */
export const ATTESA_INVALIDAZIONE_PRESENZA_MS = 2000;

/**
 * Quanto si aspetta prima di rileggere i punteggi dopo un cambio di variante o
 * di cadenza.
 *
 * Nella Sfida diretta variante e cadenza si scelgono mentre i punteggi sono
 * sotto gli occhi, e i menu cambiano a ogni click: il ritardo e' per chi scorre
 * l'elenco delle varianti, cosi' si chiede una volta sola e non una per voce
 * sfiorata. Vive qui e non piu' dentro `OnlinePlayersList` perche' ora sono in
 * due ad aspettare — la lista aperta e la riga dell'avversario gia' scelto — e
 * devono aspettare lo stesso tempo.
 */
export const RITARDO_CAMBIO_BUCKET_MS = 400;

/**
 * Quando ricontrollare che un'arena "in programma" sia davvero partita.
 *
 * `arena:started` è un annuncio solo, mandato alla stanza socket dell'arena: se
 * il socket si è riconnesso nel frattempo (schermo bloccato, cambio rete,
 * riavvio del server) quella stanza non contiene più nessuno e l'annuncio non
 * arriva mai. Passata l'ora d'inizio la pagina se lo va a chiedere da sola.
 *
 * Le attese si allargano perché un'arena può anche non partire — se il server
 * è giù, insistere ogni sei secondi non la fa partire prima. Il primo passo è
 * più lungo del giro del ciclo di vita del server (5s), o si chiederebbe in
 * anticipo su chi deve rispondere.
 */
export const ARENA_START_RECHECK_MS = [6000, 10000, 20000, 40000, 60000, 60000] as const;

/**
 * Quanto resta acceso il richiamo dei pezzi muovibili dopo un tocco a vuoto
 * (`movableHint` in game-store). Due pulsazioni da 0,75 s: l'animazione CSS finisce
 * esattamente quando lo stato si spegne, così non c'è scatto finale.
 */
export const MOVABLE_HINT_MS = 1500;

/**
 * Quanto resta annunciata l'ultima mossa in una partita alla cieca (alone sulle caselle
 * + banner con la notazione).
 *
 * È il canale che sostituisce la voce dell'arbitro: bendati, senza questo, l'unico modo
 * di sapere cosa ha giocato l'avversario sarebbe sbirciare — cioè proprio il gesto che
 * la modalità esiste per rendere raro.
 */
export const BLIND_LAST_MOVE_MS = 5000;

/*
 * `CAPTURE_STEP_MS` non abita piu' qui: e' in `@draughts/shared`
 * (`constants/animazione.ts`), insieme a `durataAnimazionePresa`. Se n'e' andata quando
 * l'orologio del server ha iniziato a scontare l'animazione all'avversario: da allora la
 * cadenza non e' piu' una faccenda del solo client, e una copia nel web sarebbe una copia
 * che puo' divergere da quella su cui si fanno i conti del tempo.
 */

/**
 * Pausa fra una mossa e l'altra quando si rigioca la soluzione di un problema.
 *
 * Si COMPONE con `CAPTURE_STEP_MS`, non lo sostituisce: la mossa i+1 parte dopo questa
 * pausa PIU' la durata dell'animazione della mossa i (vedi `tempiDellaSoluzione`). Finche'
 * era un `600 * i` nudo, la presa multipla non aveva il tempo di percorrersi e compariva
 * tutta insieme — poi la damiera restava ferma sei decimi di secondo, che e' il motivo per
 * cui il replay si leggeva come uno sfarfallio invece che come una partita.
 */
export const SOLUTION_STEP_MS = 600;

/**
 * Per quanto tempo una mossa ottimistica appena spedita ha la precedenza sullo
 * stato che il server manda al rientro (`applyReconnectResult`).
 *
 * E' una CORSA che si protegge, e una corsa dura millisecondi: la mossa e'
 * partita e l'eco non e' ancora tornata. Senza tetto la protezione diventava
 * eterna — `preMoveBoardState` lo azzera proprio l'eco, quindi se l'evento perso
 * e' quello la bandiera non si abbassa piu' e OGNI porta di rientro
 * (riconnessione, ritorno in primo piano, «Riprova») si rifiutava di riallineare
 * la damiera. Restava solo chiudere e riaprire l'app.
 *
 * Il prezzo, dichiarato: su una rete piu' lenta di cosi' un rientro puo'
 * riportare indietro il pezzo per poi rimetterlo avanti all'arrivo dell'eco. Un
 * tremolio raro su un percorso raro, contro un blocco permanente.
 */
export const SOGLIA_ECO_MS = 3000;

/**
 * Dopo quanto la sentinella (`useGuardiaSincronia`) considera ferma una damiera
 * e chiede il resync.
 *
 * ⚠️ DEVE restare maggiore di `SOGLIA_ECO_MS`, ed e' per questo che le due
 * costanti stanno una accanto all'altra: la sentinella guarisce proprio
 * chiedendo il rientro che la guardia puo' rifiutare. Con le soglie invertite
 * chiederebbe l'unica cosa che le verrebbe negata.
 */
export const SOGLIA_SENTINELLA_MS = 6000;
